import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconKey, IconLogin2, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum, denar } from '../oblika/oblika';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { NAZIVI_PAKETOV, PAKETI, type Paket } from '../seja/paket';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { SkrbniskiZaslon, StanjeInstance, ZacasnoGesloPrikaz } from './Skrbnistvo';
import { cas } from './skrbnistvo';
import { glave, obNapaki } from './zeton';
import { usePrijavaKot } from './prijavaKot';
import { AiInstance } from './AiInstance';
import { AktivnostInstance } from './AktivnostInstance';
import { NarocninaInstance } from './NarocninaInstance';

type Instanca = Shema['InstancaSkrbnik'];
type Uporabnik = Shema['UporabnikSkrbnik'];

/**
 * Instanca v skrbništvu: urejanje (naziv, domena, mesečna naročnina), naročnina s točkami, izklop (takoj odjavi vse uporabnike) in omejitve, uporabniki s
 * ponastavitvijo pozabljenega gesla in dodajanjem, izbris izklopljene instance s potrditvijo domene.
 */
export function InstancaSkrbnistvo() {
  return (
    <SkrbniskiZaslon>
      <Podrobnosti />
    </SkrbniskiZaslon>
  );
}

async function klic<T>(k: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  try {
    return await podatki(k);
  } catch (e) {
    obNapaki(e);
    throw e;
  }
}

function Podrobnosti() {
  const { companyId } = useParams();
  const id = Number(companyId);
  const instance = useQuery({
    queryKey: ['skrbnik', 'instance'],
    queryFn: ({ signal }) => klic(api.GET('/api/skrbnik/instance', { signal, headers: glave() })),
  });
  const i = instance.data?.find((x) => x.companyId === id);
  if (!i) {
    return (
      <StanjeNalaganja
        napaka={instance.error ?? (instance.data ? new NapakaApi('Instanca ne obstaja.', 'NE_OBSTAJA', 404) : null)}
        ponovi={() => void instance.refetch()}
        naslov="Instance ni bilo mogoče naložiti"
      />
    );
  }
  return <Instanca key={`${i.companyId}-${i.domena}-${i.naziv}-${i.aktivna}-${i.paket}-${i.posebnaNarocnina}`} i={i} />;
}

function Instanca({ i }: { i: Instanca }) {
  const odjemalec = useQueryClient();
  const prijavaKot = usePrijavaKot();
  const [urejeno, setUrejeno] = useState({
    naziv: i.naziv ?? '',
    domena: i.domena ?? '',
    aktivna: !!i.aktivna,
    vseFunkcionalnosti: !!i.vseFunkcionalnosti,
    paket: i.paket ?? 'PRODAJA',
    posebnaNarocnina: (i.posebnaNarocnina ?? '') as number | string,
  });
  // prazno = naročnina po ceniku paketa (znesek določi strežnik)
  const posebnaNarocnina = urejeno.posebnaNarocnina === '' ? undefined : Number(urejeno.posebnaNarocnina);
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [izklop, setIzklop] = useState(false);
  const [izbris, setIzbris] = useState(false);
  const spremenjeno =
    urejeno.naziv !== i.naziv ||
    urejeno.domena !== i.domena ||
    urejeno.aktivna !== i.aktivna ||
    urejeno.vseFunkcionalnosti !== i.vseFunkcionalnosti ||
    urejeno.paket !== i.paket ||
    posebnaNarocnina !== (i.posebnaNarocnina ?? undefined);

  const shrani = async () => {
    setIzklop(false);
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      await klic(
        api.PUT('/api/skrbnik/instance/{companyId}', {
          headers: glave(),
          params: { path: { companyId: i.companyId! } },
          body: { ...i, ...urejeno, posebnaNarocnina },
        }),
      );
      notifications.show({ color: 'green', message: 'Instanca je shranjena.' });
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik'] });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  // izklop odjavi vse uporabnike instance – vprašamo
  const potrdi = () => (i.aktivna && !urejeno.aktivna ? setIzklop(true) : void shrani());

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/skrbnistvo"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Skrbništvo
      </Button>
      <GlavaZaslona
        naslov={i.naziv ?? ''}
        opis={`#${i.companyId} · ${i.domena} · ${i.shema}`}
        dejanja={
          <Group gap="sm">
            <StanjeInstance i={i} />
            <Button
              leftSection={<IconLogin2 size={18} />}
              disabled={!i.aktivna || !i.uporabnikov}
              loading={prijavaKot.dela(i.companyId)}
              onClick={() => void prijavaKot.prijavi(i.companyId!)}
            >
              Prijavi se v instanco
            </Button>
          </Group>
        }
      />
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Kartica naslov="Instanca">
            <Stack gap="sm">
              <TextInput
                label="Naziv podjetja"
                value={urejeno.naziv}
                onChange={(e) => setUrejeno({ ...urejeno, naziv: e.currentTarget.value })}
                error={napake.naziv}
              />
              <TextInput
                label="Domena"
                description="Pri izbiri podjetja ob prijavi"
                value={urejeno.domena}
                onChange={(e) => setUrejeno({ ...urejeno, domena: e.currentTarget.value.toLowerCase() })}
                error={napake.domena}
              />
              <Switch
                label="Aktivna"
                description="Izklopljena instanca: nihče se ne more prijaviti, prijavljeni so takoj odjavljeni."
                checked={urejeno.aktivna}
                onChange={(e) => setUrejeno({ ...urejeno, aktivna: e.currentTarget.checked })}
              />
              <Switch
                label="Vse funkcionalnosti"
                description="Izklopljeno: programske funkcije so omejene (hitra dejanja onemogočena, brez pregleda poslovanja)."
                checked={urejeno.vseFunkcionalnosti}
                onChange={(e) => setUrejeno({ ...urejeno, vseFunkcionalnosti: e.currentTarget.checked })}
              />
              <Select
                label="Paket"
                description="Moduli instance. Posebna naročnina spodaj ostane, kot je vpisana."
                data={PAKETI.map((p) => ({ value: p, label: NAZIVI_PAKETOV[p] }))}
                value={urejeno.paket}
                onChange={(v) => v && setUrejeno({ ...urejeno, paket: v as Paket })}
                allowDeselect={false}
                error={napake.paket}
              />
              <NumberInput
                label="Mesečna naročnina (€ z DDV)"
                description="Prazno = naročnina po ceniku paketa."
                placeholder="Po ceniku"
                value={urejeno.posebnaNarocnina}
                onChange={(v) => setUrejeno({ ...urejeno, posebnaNarocnina: v })}
                min={0}
                decimalScale={2}
                decimalSeparator=","
                thousandSeparator="."
                hideControls
                error={napake.posebnaNarocnina}
              />
              {napaka && (
                <Alert color="red" variant="light" role="alert">
                  {napaka}
                </Alert>
              )}
              <Group justify="flex-end">
                <Button onClick={potrdi} loading={dela} disabled={!spremenjeno}>
                  Shrani
                </Button>
              </Group>
            </Stack>
          </Kartica>
          <Kartica naslov="Uporaba">
            <Podatki
              vrstice={[
                ['Paket', i.paket ? NAZIVI_PAKETOV[i.paket] : ''],
                ['Mesečna naročnina', `${denar(i.narocnina)} €${i.posebnaNarocnina != null ? ' (posebna)' : ''}`],
                ['Uporabnikov', String(i.uporabnikov ?? 0)],
                ['Računov', String(i.racunov ?? 0)],
                ['Zadnji račun', datum(i.zadnjiRacun)],
                ['Zadnja prijava', cas(i.zadnjaPrijava)],
                ['Zadnja aktivnost', cas(i.zadnjaAktivnost)],
                ['Aktivni dnevi (30 dni)', String(i.aktivnihDni ?? 0)],
                ['Ustvarjena', cas(i.ustvarjena)],
              ]}
            />
          </Kartica>
        </SimpleGrid>
        <NarocninaInstance companyId={i.companyId!} />
        <AiInstance companyId={i.companyId!} />
        <Uporabniki i={i} />
        <AktivnostInstance i={i} />
        <Kartica naslov="Izbris instance">
          <Group justify="space-between" gap="sm">
            <Text size="sm" c="dimmed" style={{ flex: '1 1 320px' }}>
              Izbriše vse podatke podjetja (račune, vozila, osebe …) in uporabnike, ki nimajo dostopa do drugega
              podjetja. Tega ni mogoče razveljaviti. Mogoče je samo pri izklopljeni instanci.
            </Text>
            <Button
              color="red"
              variant="light"
              leftSection={<IconTrash size={18} />}
              disabled={i.aktivna}
              onClick={() => setIzbris(true)}
            >
              Izbriši instanco
            </Button>
          </Group>
        </Kartica>
      </Stack>
      <Modal opened={izklop} onClose={() => setIzklop(false)} title="Izklop instance" radius="lg">
        <Stack gap="sm">
          <Text size="sm">
            Vsi uporabniki podjetja {i.naziv} bodo takoj odjavljeni in se ne bodo mogli več prijaviti, dokler instance
            ne vklopite. Podatki ostanejo.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setIzklop(false)}>
              Prekliči
            </Button>
            <Button color="red" onClick={() => void shrani()} loading={dela}>
              Izklopi
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Izbris i={i} odprto={izbris} zapri={() => setIzbris(false)} />
    </ZaslonSeznama>
  );
}

function Izbris({ i, odprto, zapri }: { i: Instanca; odprto: boolean; zapri: () => void }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [potrditev, setPotrditev] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const izbrisi = async () => {
    setDela(true);
    setNapaka(null);
    try {
      await klic(
        api.DELETE('/api/skrbnik/instance/{companyId}', {
          headers: glave(),
          params: { path: { companyId: i.companyId! }, query: { potrditev } },
        }),
      );
      notifications.show({ color: 'green', message: `Instanca ${i.naziv} je izbrisana.` });
      odjemalec.removeQueries({ queryKey: ['skrbnik', 'uporabniki', i.companyId] });
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'instance'] });
      void navigate('/skrbnistvo', { replace: true });
    } catch (e) {
      setNapaka(e instanceof NapakaApi ? (e.polja.potrditev ?? e.message) : sporocilo(e));
    } finally {
      setDela(false);
    }
  };
  return (
    <Modal opened={odprto} onClose={() => !dela && zapri()} title={`Izbris instance ${i.naziv}`} radius="lg">
      <Stack gap="sm">
        <Alert color="red" variant="light">
          Vsi podatki podjetja bodo trajno izbrisani. Tega ni mogoče razveljaviti.
        </Alert>
        <TextInput
          label={`Za potrditev vpišite domeno »${i.domena}«`}
          value={potrditev}
          onChange={(e) => setPotrditev(e.currentTarget.value)}
          error={napaka}
          data-autofocus
          autoComplete="off"
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela}>
            Prekliči
          </Button>
          <Button
            color="red"
            onClick={() => void izbrisi()}
            loading={dela}
            disabled={potrditev.trim().toLowerCase() !== (i.domena ?? '').toLowerCase()}
          >
            Trajno izbriši
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function Uporabniki({ i }: { i: Instanca }) {
  const odjemalec = useQueryClient();
  const prijavaKot = usePrijavaKot();
  const [geslo, setGeslo] = useState<{ ime?: string; geslo?: string; opis: string } | null>(null);
  const [ponastavi, setPonastavi] = useState<Uporabnik | null>(null);
  const [nov, setNov] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const uporabniki = useQuery({
    queryKey: ['skrbnik', 'uporabniki', i.companyId],
    queryFn: ({ signal }) =>
      klic(
        api.GET('/api/skrbnik/instance/{companyId}/uporabniki', {
          signal,
          headers: glave(),
          params: { path: { companyId: i.companyId! } },
        }),
      ),
  });

  const potrdiPonastavitev = async () => {
    if (!ponastavi) {
      return;
    }
    setDela(true);
    setNapaka(null);
    try {
      const g = await klic(
        api.POST('/api/skrbnik/instance/{companyId}/uporabniki/{prijavaId}/geslo', {
          headers: glave(),
          params: { path: { companyId: i.companyId!, prijavaId: ponastavi.prijavaId! } },
        }),
      );
      setGeslo({
        ime: ponastavi.uporabniskoIme,
        geslo: g.zacasnoGeslo,
        opis: `Uporabnik ${ponastavi.ime ?? ''} ${ponastavi.priimek ?? ''} je odjavljen z vseh naprav in odklenjen. S tem začasnim geslom se prijavi in ga takoj zamenja.`,
      });
      setPonastavi(null);
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik', 'uporabniki', i.companyId] });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica
      naslov="Uporabniki"
      dejanja={
        <Button size="sm" variant="default" leftSection={<IconPlus size={16} />} onClick={() => setNov(true)}>
          Dodaj
        </Button>
      }
    >
      {!uporabniki.data ? (
        <StanjeNalaganja
          napaka={uporabniki.error}
          ponovi={() => void uporabniki.refetch()}
          naslov="Uporabnikov ni bilo mogoče naložiti"
        />
      ) : (
        <Table.ScrollContainer minWidth={1000}>
          <Table verticalSpacing={8}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Uporabnik</Table.Th>
                <Table.Th>Uporabniško ime</Table.Th>
                <Table.Th>Stanje</Table.Th>
                <Table.Th>Zadnja prijava</Table.Th>
                <Table.Th>Aktivnost (30 dni)</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {uporabniki.data.map((u) => (
                <Table.Tr key={u.prijavaId}>
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {[u.ime, u.priimek].filter(Boolean).join(' ')}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {u.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>{u.uporabniskoIme}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {!u.prijava && <Badge color="gray">brez prijave</Badge>}
                      {u.prijava && !u.aktiven && <Badge color="gray">neaktiven</Badge>}
                      {u.zacasnoGeslo && <Badge color="yellow">začasno geslo</Badge>}
                      {u.zaklenjenDo && <Badge color="red">zaklenjen do {cas(u.zaklenjenDo)}</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td>{cas(u.zadnjaPrijava)}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{u.aktivnihDni ? `${u.aktivnihDni} dni · ${u.zahtev} zahtev` : ''}</Text>
                    <Text size="xs" c="dimmed">
                      {cas(u.zadnjaAktivnost)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    {u.prijava && (
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconLogin2 size={14} />}
                          disabled={!i.aktivna || !u.aktiven}
                          loading={prijavaKot.dela(i.companyId, u.prijavaId)}
                          onClick={() => void prijavaKot.prijavi(i.companyId!, u.prijavaId)}
                        >
                          Prijavi se kot
                        </Button>
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconKey size={14} />}
                          onClick={() => setPonastavi(u)}
                        >
                          Ponastavi geslo
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Modal
        opened={ponastavi !== null}
        onClose={() => !dela && setPonastavi(null)}
        title="Ponastavi geslo"
        radius="lg"
      >
        <Stack gap="sm">
          <Text size="sm">
            Uporabnik <b>{ponastavi?.uporabniskoIme}</b> dobi novo začasno geslo, ki ga ob prijavi zamenja. Odjavljen bo
            z vseh naprav, staro geslo ne bo več veljalo.
          </Text>
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setPonastavi(null)} disabled={dela}>
              Prekliči
            </Button>
            <Button onClick={() => void potrdiPonastavitev()} loading={dela}>
              Ponastavi
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={geslo !== null} onClose={() => setGeslo(null)} title="Začasno geslo" radius="lg">
        {geslo && (
          <ZacasnoGesloPrikaz
            opis={geslo.opis}
            uporabniskoIme={geslo.ime}
            geslo={geslo.geslo}
            zapri={() => setGeslo(null)}
          />
        )}
      </Modal>
      <NovUporabnik
        i={i}
        odprto={nov}
        zapri={() => setNov(false)}
        dodan={(ime, g) => {
          setNov(false);
          setGeslo({
            ime,
            geslo: g,
            opis: 'Uporabnik je dodan. S tem začasnim geslom se prijavi in ga ob prvi prijavi zamenja.',
          });
          void odjemalec.invalidateQueries({ queryKey: ['skrbnik'] });
        }}
      />
    </Kartica>
  );
}

function NovUporabnik({
  i,
  odprto,
  zapri,
  dodan,
}: {
  i: Instanca;
  odprto: boolean;
  zapri: () => void;
  dodan: (ime: string, geslo?: string) => void;
}) {
  const [u, setU] = useState({ uporabniskoIme: '', ime: '', priimek: '', email: '' });
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const dodaj = async () => {
    setDela(true);
    setNapaka(null);
    try {
      const g = await klic(
        api.POST('/api/skrbnik/instance/{companyId}/uporabniki', {
          headers: glave(),
          params: { path: { companyId: i.companyId! } },
          body: { ...u, casSeje: 60 },
        }),
      );
      dodan(u.uporabniskoIme, g.zacasnoGeslo);
      setU({ uporabniskoIme: '', ime: '', priimek: '', email: '' });
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };
  return (
    <Modal opened={odprto} onClose={() => !dela && zapri()} title={`Nov uporabnik – ${i.naziv}`} radius="lg">
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <TextInput
            label="Uporabniško ime"
            value={u.uporabniskoIme}
            onChange={(e) => setU({ ...u, uporabniskoIme: e.currentTarget.value })}
            data-autofocus
          />
          <TextInput label="E-pošta" value={u.email} onChange={(e) => setU({ ...u, email: e.currentTarget.value })} />
          <TextInput label="Ime" value={u.ime} onChange={(e) => setU({ ...u, ime: e.currentTarget.value })} />
          <TextInput
            label="Priimek"
            value={u.priimek}
            onChange={(e) => setU({ ...u, priimek: e.currentTarget.value })}
          />
        </SimpleGrid>
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri} disabled={dela}>
            Prekliči
          </Button>
          <Button onClick={() => void dodaj()} loading={dela}>
            Dodaj
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
